import { useEffect, useMemo, useState } from 'react';
import {
  Table, Card, Input, Tag, Typography, Progress, Drawer, Button, Empty, Spin,
  Row, Col, Statistic, Divider, Tooltip, Badge, Segmented, message,
} from 'antd';
import {
  SafetyCertificateOutlined, SearchOutlined, WarningOutlined, AuditOutlined,
  FundProjectionScreenOutlined, DollarOutlined, FileExclamationOutlined,
  HeartOutlined, BankOutlined, TeamOutlined, ShoppingOutlined, RiseOutlined,
  CheckCircleOutlined, ExportOutlined, EyeOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';

const { Title, Text, Paragraph } = Typography;
const fmt = (v) => Number(v || 0).toLocaleString('uz-UZ');

const RISK = {
  high: { color: '#cf1322', bg: '#fff1f0', border: '#ffa39e', label: 'Yuqori xavf' },
  medium: { color: '#d46b08', bg: '#fff7e6', border: '#ffd591', label: "O'rta xavf" },
  low: { color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f', label: 'Past xavf' },
};
const GRADE_COLOR = { A: '#389e0d', B: '#7cb305', C: '#d4b106', D: '#d46b08', F: '#cf1322' };
const PRIORITY_COLOR = { 'Shoshilinch': '#cf1322', 'Yuqori': '#d46b08', "O'rta": '#d4b106', 'Past': '#389e0d' };
const SEV_COLOR = { high: '#cf1322', medium: '#d46b08', low: '#8c8c8c' };

// Audit xulosasi (grade asosida)
function recommendation(p) {
  if (p.risk_score >= 60) return { type: 'error', text: `Yuqori soliqdan qochish ehtimoli aniqlandi. Taxminan ${fmt(p.predicted_undeclared)} so'm deklaratsiya qilinmagan daromad va ${p.report_active} ta fuqaro shikoyati mavjud. Shoshilinch kameral tekshiruv tavsiya etiladi.` };
  if (p.risk_score >= 40) return { type: 'warning', text: `O'rtacha xavf darajasi. Daromad deklaratsiyasi va to'y rejalaridagi izlar mos kelmaydi. Hujjatlarni so'rab, qo'shimcha tahlil o'tkazish tavsiya etiladi.` };
  if (p.risk_score >= 20) return { type: 'warning', text: `Past-o'rta xavf. Ayrim ko'rsatkichlar e'tiborni talab qiladi, lekin jiddiy buzilish belgilari kam.` };
  return { type: 'success', text: `Tashkilot faoliyati shaffof ko'rinadi. Jiddiy soliq xavfi aniqlanmadi. Monitoring rejimida kuzatuv yetarli.` };
}

function KpiCard({ icon, title, value, suffix, color, sub }) {
  return (
    <Card bordered={false} style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.05)', height: '100%' }} bodyStyle={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: `${color}15`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{title}</Text>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--qk-text)', lineHeight: 1.2 }}>
            {value}{suffix && <span style={{ fontSize: 13, fontWeight: 500, color: '#888' }}> {suffix}</span>}
          </div>
          {sub && <Text type="secondary" style={{ fontSize: 11 }}>{sub}</Text>}
        </div>
      </div>
    </Card>
  );
}

function GradeBadge({ grade, size = 44 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: GRADE_COLOR[grade] || '#999',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.45, fontWeight: 800, boxShadow: `0 2px 8px ${GRADE_COLOR[grade]}55`,
    }}>{grade}</div>
  );
}

function SectionTitle({ icon, children, extra }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: 'var(--qk-text)' }}>
        <span style={{ color: '#fa8c16' }}>{icon}</span>{children}
      </div>
      {extra}
    </div>
  );
}

export default function TaxRiskAnalysis() {
  const [list, setList] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admingov/tax/risk-list', { params: search ? { search } : {} });
      setList(r.data.data || []);
      setOverview(r.data.overview || null);
    } catch { message.error("Ma'lumotlarni yuklashda xatolik"); }
    setLoading(false);
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(fetchList, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openDetail = async (id) => {
    setDrawerOpen(true); setDetailLoading(true); setDetail(null);
    try {
      const r = await api.get(`/admingov/tax/risk/${id}`);
      setDetail(r.data.data);
    } catch { message.error('Tahlilni yuklashda xatolik'); }
    setDetailLoading(false);
  };

  const filtered = useMemo(
    () => list.filter(p => levelFilter === 'all' || p.risk_level === levelFilter),
    [list, levelFilter]
  );

  const exportCSV = () => {
    const headers = ['Tashkilot', 'STIR', 'Egasi', 'Hudud', 'Buyurtmalar', 'Daromad', "To'lov", 'Shikoyat', "To'y izi", 'Ball', 'Daraja', 'Prognoz (yashirin)'];
    const rows = filtered.map(p => [p.name, p.stir || '', p.owner_name, p.region, p.total_orders, p.total_revenue, p.total_payments, p.report_active, p.wedding_external, p.risk_score, p.grade, p.predicted_undeclared]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'havf_tahlili.csv'; a.click();
  };

  const columns = [
    {
      title: 'Tashkilot', dataIndex: 'name', key: 'name',
      render: (v, r) => (
        <div>
          <Text strong>{v}</Text>
          <div style={{ fontSize: 11, color: '#999' }}>
            STIR: {r.stir || <Tag color="red" style={{ fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>yo'q</Tag>} · {r.owner_name}
          </div>
        </div>
      ),
    },
    { title: 'Hudud', dataIndex: 'region', key: 'region', width: 110, render: v => v || '—' },
    {
      title: 'Faoliyat', key: 'activity', width: 140,
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div><ShoppingOutlined style={{ color: '#5F33E1' }} /> {r.total_orders} buyurtma</div>
          <div style={{ color: '#389e0d' }}>{fmt(r.total_revenue)} so'm</div>
        </div>
      ),
    },
    {
      title: 'Tashqi izlar', key: 'external', width: 130, align: 'center',
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          <Tooltip title="Soliq hamkor (fuqaro) shikoyatlari">
            <Tag icon={<FileExclamationOutlined />} color={r.report_active ? 'red' : 'default'} style={{ margin: 0 }}>{r.report_active}</Tag>
          </Tooltip>
          <Tooltip title="To'y rejasidagi tizimdan tashqari summa">
            <Tag icon={<HeartOutlined />} color={r.wedding_external ? 'volcano' : 'default'} style={{ margin: 0 }}>{r.wedding_external ? fmt(r.wedding_external) : 0}</Tag>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Prognoz (yashirin daromad)', dataIndex: 'predicted_undeclared', key: 'pred', width: 150,
      sorter: (a, b) => a.predicted_undeclared - b.predicted_undeclared,
      render: v => <Text strong style={{ color: v > 0 ? '#cf1322' : '#389e0d' }}>{fmt(v)} so'm</Text>,
    },
    {
      title: 'Xavf bali', dataIndex: 'risk_score', key: 'score', width: 170,
      defaultSortOrder: 'descend', sorter: (a, b) => a.risk_score - b.risk_score,
      render: (v, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GradeBadge grade={r.grade} size={34} />
          <div style={{ flex: 1 }}>
            <Progress percent={v} size="small" strokeColor={RISK[r.risk_level].color} format={() => `${v}/100`} />
            <Tag color={RISK[r.risk_level].color} style={{ fontSize: 10, marginTop: 2 }}>{RISK[r.risk_level].label}</Tag>
          </div>
        </div>
      ),
    },
    {
      title: '', key: 'act', width: 110, fixed: 'right',
      render: (_, r) => <Button type="primary" ghost size="small" icon={<EyeOutlined />} onClick={() => openDetail(r.id)}>Audit</Button>,
    },
  ];

  return (
    <div>
      {/* Sarlavha */}
      <div style={{ background: 'linear-gradient(120deg,#0b1f3a 0%,#13386b 100%)', borderRadius: 16, padding: '22px 26px', marginBottom: 20, color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <SafetyCertificateOutlined style={{ fontSize: 34, color: '#fa8c16' }} />
            <div>
              <Title level={3} style={{ color: '#fff', margin: 0 }}>Havf tahlili — Soliq Audit Tizimi</Title>
              <Text style={{ color: 'rgba(255,255,255,0.65)' }}>
                Xizmat faoliyati, to'y rejalari va fuqaro shikoyatlari asosida soliqdan qochish va qonun buzilishi prognozi
              </Text>
            </div>
          </div>
          <Button icon={<ExportOutlined />} onClick={exportCSV}>CSV hisobot</Button>
        </div>
      </div>

      {/* KPI */}
      <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
        <Col xs={12} md={6}><KpiCard icon={<BankOutlined />} color="#1677ff" title="Jami tashkilotlar" value={overview?.total_orgs ?? '—'} sub="tizimda ro'yxatda" /></Col>
        <Col xs={12} md={6}><KpiCard icon={<WarningOutlined />} color="#cf1322" title="Yuqori xavfli" value={overview?.high_risk ?? '—'} sub={`${overview?.medium_risk ?? 0} o'rta · ${overview?.low_risk ?? 0} past`} /></Col>
        <Col xs={12} md={6}><KpiCard icon={<FundProjectionScreenOutlined />} color="#d46b08" title="Prognoz yashirin daromad" value={fmt(overview?.total_predicted_undeclared)} suffix="so'm" sub="deklaratsiya qilinmagan (taxmin)" /></Col>
        <Col xs={12} md={6}><KpiCard icon={<DollarOutlined />} color="#389e0d" title="Taxminiy soliq qarzi" value={fmt(overview?.total_estimated_tax)} suffix="so'm" sub={`${overview?.total_reports ?? 0} ta faol shikoyat`} /></Col>
      </Row>

      {/* Filtrlar */}
      <Card bordered={false} style={{ borderRadius: 14, marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: 14 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input placeholder="Tashkilot nomi yoki STIR bo'yicha qidirish" prefix={<SearchOutlined />} allowClear value={search} onChange={e => setSearch(e.target.value)} style={{ width: 320, maxWidth: '100%' }} />
          <Segmented
            value={levelFilter}
            onChange={setLevelFilter}
            options={[
              { label: 'Barchasi', value: 'all' },
              { label: `Yuqori`, value: 'high' },
              { label: "O'rta", value: 'medium' },
              { label: 'Past', value: 'low' },
            ]}
          />
          <Text type="secondary" style={{ marginLeft: 'auto' }}>Ball bo'yicha kamayish tartibida saralangan</Text>
        </div>
      </Card>

      {/* Jadval */}
      <Card bordered={false} style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: 8 }}>
        <Table
          dataSource={filtered} columns={columns} rowKey="id" loading={loading} size="middle"
          scroll={{ x: 980 }} pagination={{ pageSize: 12, hideOnSinglePage: true }}
          onRow={(r) => ({ onClick: () => openDetail(r.id), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <Empty description="Tashkilot topilmadi" /> }}
        />
      </Card>

      {/* AUDIT DRAWER */}
      <Drawer
        open={drawerOpen} onClose={() => setDrawerOpen(false)} width={Math.min(820, window.innerWidth)}
        title={<span><AuditOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Tashkilot auditi</span>}
        styles={{ body: { background: '#f7f8fa', padding: 18 } }}
      >
        {detailLoading && <Spin style={{ display: 'block', margin: '60px auto' }} size="large" />}
        {!detailLoading && detail && <AuditDetail p={detail} />}
      </Drawer>
    </div>
  );
}

function AuditDetail({ p }) {
  const rec = recommendation(p);
  const maxTrend = Math.max(1, ...(p.monthly_trend || []).map(m => Number(m.revenue)));

  return (
    <div>
      {/* Header card */}
      <Card bordered={false} style={{ borderRadius: 14, marginBottom: 14 }} bodyStyle={{ padding: 18 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={4} style={{ margin: 0 }}>{p.name}</Title>
            <Text type="secondary">
              STIR: {p.stir || <Tag color="red">mavjud emas</Tag>} · Egasi: {p.owner_name} · {p.owner_phone || '—'}
            </Text>
            <div style={{ marginTop: 6 }}>
              <Tag color={PRIORITY_COLOR[p.audit_priority]} icon={<ThunderboltOutlined />}>Audit ustuvorligi: {p.audit_priority}</Tag>
              <Tag color={RISK[p.risk_level].color}>{RISK[p.risk_level].label}</Tag>
              <Tag>{p.region || '—'}{p.district ? `, ${p.district}` : ''}</Tag>
            </div>
          </Col>
          <Col>
            <div style={{ textAlign: 'center' }}>
              <Progress type="dashboard" percent={p.risk_score} size={120} strokeColor={RISK[p.risk_level].color}
                format={() => <div><div style={{ fontSize: 26, fontWeight: 800, color: RISK[p.risk_level].color }}>{p.risk_score}</div><div style={{ fontSize: 11, color: '#999' }}>xavf bali</div></div>} />
            </div>
          </Col>
          <Col>
            <div style={{ textAlign: 'center' }}>
              <GradeBadge grade={p.grade} size={64} />
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>reyting</div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Xulosa */}
      <Card bordered={false} style={{ borderRadius: 14, marginBottom: 4, borderLeft: `4px solid ${rec.type === 'error' ? '#cf1322' : rec.type === 'warning' ? '#d46b08' : '#389e0d'}` }} bodyStyle={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          {rec.type === 'success' ? <CheckCircleOutlined style={{ color: '#389e0d', fontSize: 20 }} /> : <WarningOutlined style={{ color: rec.type === 'error' ? '#cf1322' : '#d46b08', fontSize: 20 }} />}
          <div>
            <Text strong>Audit xulosasi va tavsiya</Text>
            <Paragraph style={{ margin: '4px 0 0' }}>{rec.text}</Paragraph>
          </div>
        </div>
      </Card>

      {/* Moliyaviy ko'rsatkichlar */}
      <SectionTitle icon={<DollarOutlined />}>Moliyaviy ko'rsatkichlar</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 14 }}><Statistic title="Buyurtmalar daromadi" value={p.total_revenue} formatter={v => fmt(v)} suffix="so'm" valueStyle={{ fontSize: 18 }} /></Card></Col>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 14 }}><Statistic title="Deklaratsiya (to'lov)" value={p.total_payments} formatter={v => fmt(v)} suffix="so'm" valueStyle={{ fontSize: 18, color: '#389e0d' }} /></Card></Col>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 12, background: 'rgba(255,77,79,0.12)' }} bodyStyle={{ padding: 14 }}><Statistic title="Prognoz yashirin daromad" value={p.predicted_undeclared} formatter={v => fmt(v)} suffix="so'm" valueStyle={{ fontSize: 18, color: '#cf1322' }} /></Card></Col>
        <Col xs={12} md={6}><Card bordered={false} style={{ borderRadius: 12, background: 'rgba(245,158,11,0.12)' }} bodyStyle={{ padding: 14 }}><Statistic title="Taxminiy soliq qarzi (12%)" value={p.estimated_tax_due} formatter={v => fmt(v)} suffix="so'm" valueStyle={{ fontSize: 18, color: '#d46b08' }} /></Card></Col>
      </Row>
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={8}><Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 12 }}><Statistic title="Xizmatlar" value={p.services_count} prefix={<ShoppingOutlined />} valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8}><Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 12 }}><Statistic title="Xodimlar" value={p.emp_count} prefix={<TeamOutlined />} valueStyle={{ fontSize: 16 }} suffix={p.total_salary ? '' : '(maosh 0)'} /></Card></Col>
        <Col xs={8}><Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 12 }}><Statistic title="Platforma to'lovi" value={p.platform_payments} formatter={v => fmt(v)} valueStyle={{ fontSize: 16 }} /></Card></Col>
      </Row>

      {/* Xavf omillari */}
      <SectionTitle icon={<WarningOutlined />}>Xavf omillari ({p.factors.length})</SectionTitle>
      <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: p.factors.length ? 8 : 16 }}>
        {p.factors.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Xavf omillari aniqlanmadi — faoliyat shaffof" />}
        {p.factors.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', borderBottom: i < p.factors.length - 1 ? '1px solid var(--qk-border)' : 'none' }}>
            <Badge color={SEV_COLOR[f.severity]} />
            <div style={{ flex: 1 }}>
              <Text strong>{f.label}</Text>
              {f.detail && <div style={{ fontSize: 12, color: '#888' }}>{f.detail}</div>}
            </div>
            <Tag color={SEV_COLOR[f.severity]} style={{ fontWeight: 700 }}>+{f.points} ball</Tag>
          </div>
        ))}
      </Card>

      {/* To'y rejasidagi izlar */}
      <SectionTitle icon={<HeartOutlined />} extra={<Tag color="volcano">{p.wedding_matches.length} ta moslik</Tag>}>To'y rejalaridagi izlar (xizmat nomi mos)</SectionTitle>
      <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: p.wedding_matches.length ? 0 : 16 }}>
        {p.wedding_matches.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Mos keluvchi to'y reja izlari yo'q" /> : (
          <Table size="small" pagination={false} rowKey="id" dataSource={p.wedding_matches} columns={[
            { title: 'Xizmat nomi', dataIndex: 'vendor_name' },
            { title: 'Turi', dataIndex: 'service_type', width: 100, render: v => v || '—' },
            { title: 'Summa', dataIndex: 'amount', width: 120, render: v => `${fmt(v)} so'm` },
            { title: "To'langan", dataIndex: 'paid_amount', width: 120, render: v => `${fmt(v)} so'm` },
            { title: 'Holat', dataIndex: 'payment_status', width: 90, render: v => <Tag color={v === 'paid' ? 'green' : v === 'partial' ? 'orange' : 'red'}>{v}</Tag> },
            { title: 'Manba', dataIndex: 'is_from_system', width: 90, render: v => v ? <Tag color="blue">Tizim</Tag> : <Tag color="volcano">Tashqi</Tag> },
          ]} />
        )}
      </Card>

      {/* Soliq hamkor shikoyatlari */}
      <SectionTitle icon={<FileExclamationOutlined />} extra={<Tag color="red">{p.report_matches.length} ta shikoyat</Tag>}>Soliq hamkor shikoyatlari (xizmat nomi mos)</SectionTitle>
      <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: p.report_matches.length ? 0 : 16 }}>
        {p.report_matches.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Mos keluvchi shikoyat yo'q" /> : (
          <Table size="small" pagination={false} rowKey="id" dataSource={p.report_matches} expandable={{
            expandedRowRender: r => <Text type="secondary">{r.details || "Tafsilot yo'q"}</Text>,
          }} columns={[
            { title: 'Xizmat nomi', dataIndex: 'vendor_name' },
            { title: 'Turi', dataIndex: 'service_type_name', width: 110, render: v => v || '—' },
            { title: 'Hudud', dataIndex: 'region', width: 110, render: v => v || '—' },
            { title: 'Holat', dataIndex: 'status', width: 110, render: v => <Tag color={v === 'confirmed' ? 'red' : v === 'rejected' ? 'default' : 'orange'}>{v === 'confirmed' ? 'Tasdiqlangan' : v === 'rejected' ? 'Rad etilgan' : 'Ko\'rib chiqilmoqda'}</Tag> },
          ]} />
        )}
      </Card>

      {/* Xizmatlar */}
      <SectionTitle icon={<ShoppingOutlined />}>Tashkilot xizmatlari ({p.services.length})</SectionTitle>
      <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 12 }}>
        {p.services.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Xizmat yo'q" /> :
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {p.services.map(s => <Tag key={s.id} color="purple" style={{ padding: '4px 10px', fontSize: 13 }}>{s.name} <Text type="secondary" style={{ fontSize: 11 }}>· {s.type_name}</Text></Tag>)}
          </div>}
      </Card>

      {/* Oylik trend */}
      {(p.monthly_trend || []).length > 0 && (
        <>
          <SectionTitle icon={<RiseOutlined />}>Oylik buyurtma trendi (12 oy)</SectionTitle>
          <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 130 }}>
              {p.monthly_trend.map((m, i) => (
                <Tooltip key={i} title={`${m.month}: ${m.count} buyurtma · ${fmt(m.revenue)} so'm`}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: '100%', maxWidth: 34, height: `${Math.max(4, (Number(m.revenue) / maxTrend) * 100)}px`, background: 'linear-gradient(180deg,#5F33E1,#8B5CF6)', borderRadius: '4px 4px 0 0' }} />
                    <Text style={{ fontSize: 9, color: '#999' }}>{m.month.slice(5)}</Text>
                  </div>
                </Tooltip>
              ))}
            </div>
          </Card>
        </>
      )}

      <Divider />
      <Text type="secondary" style={{ fontSize: 11 }}>
        * Ushbu tahlil tizimdagi buyurtma, to'lov, xodim, to'y rejasi va fuqaro shikoyatlari ma'lumotlariga asoslangan avtomatik prognoz bo'lib, yakuniy soliq qarori uchun rasmiy tekshiruv talab etiladi.
      </Text>
    </div>
  );
}
